import React, { useEffect, useMemo, useState } from 'react';
import FrontLayout from '@layouts/FrontLayout';
import { NextPageContextWithRedux } from '@/types/store/NextPageContextWithRedux';
import queryStringToFilters from '@/components/app/search/queryStringToFilters';
import fetchSearch, {
  fetchLabelizedMainCategories,
} from '@/components/app/search/fetchSearch';
import withUser from '@/withUser';
import withPrivateGroups from '@/withPrivateGroups';
import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import {
  Box,
  Button,
  Icon,
  Text,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import Head from 'next/head';
import Header, { NavCategory, NavSubCategory } from '@components/Header';
import Breadcrumb, {
  BreadcrumbItem,
  BreadcrumbItemProps,
} from '@components/app/Breadcrumb';
import UIWidth from '@components/presentational/UIWidth';
import Chapo from '@components/presentational/Chapo';
import MobileBottomNav, {
  mobileBottomNavStyle,
} from '@components/presentational/MobileBottomNav';
import { PiShuffleThin } from 'react-icons/pi';
import SearchApp from '@components/algolia/SearchApp';
import { useSubHeaderCategoriesFromAlgoliaFacetSearch } from '@components/app/hooks/useResource';
import HeaderResourceForm from '@components/HeaderResourceForm';
import RessourcesPicto from '@/components/presentational/img/icons/RessourcesPicto';
import resourceTranslation from '@/translations/resource.fr.yml';
import DoubleTextBanner from '@components/presentational/Banners/DoubleTextBanner';
import { useRouter } from 'next/router';

const ITEMS_PER_PAGE = 20;

const categoryDescriptions = {
  'category.fle_communication':
    'Découvrez notre sélection de ressources et fiches pédagogiques FLE dédiée aux questions linguistiques pour diversifer vos supports de classe et travailler la communication, la grammaire, le vocabulaire et la phonétique de manière ludique.',
  'category.french_university':
    'Explorez les ressources dédiées au Français sur objectifs universitaires, conçues pour soutenir la progression académique en français et préparer efficacement aux études universitaires dans un environnement francophone.',
  'category.french_professional':
    'Découvrez les ressources spécialisées en Français professionnel, élaborées pour répondre aux besoins spécifiques des adultes dans différents domaines, les aidant à développer leurs compétences pour une communication efficace dans le monde du travail.',
  'category.dnl':
    "Explorez les ressources dédiées à l'enseignement bilingue en français, conçues pour renforcer votre pratique pédagogique et votre maîtrise des contenus disciplinaires en français dans un contexte plurilingue.",
  'category.alphabetization':
    "Découvrez les ressources dédiées à l'alphabétisation et la lutte contre l'illettrisme, conçues pour soutenir les efforts visant à renforcer les compétences fondamentales auprès de publics adultes",
  'category.french_training':
    'Découvrez les ressources dédiées aux certifications et tests de langue française, élaborées pour vous accompagner dans la préparation et la réussite des examens linguistiques.',
};

function CategoryDescription({ category }: { category: string }) {
  return (
    <Text fontSize={['1rem', '', '1.25rem']} mb="2rem">
      {category === 'category.fle_culture' && (
        <>
          Explorez les <b>ressources et exercices FLE</b> sur des thématiques
          culturelles et sociétales variées pour proposer des activités et jeux
          en lien avec votre programme de cours ou dans le cadre d'ateliers
          extra-scolaires.
        </>
      )}
      {categoryDescriptions[category]}
    </Text>
  );
}

function CategorySEOContent({ category }: { category: string }) {
  return (
    <Box mt="2rem">
      {category === 'category.fle_culture' && (
        <DoubleTextBanner
          colorLeft="tuile.100"
          colorTextLeft="inherit"
          colorRight="tuile.10"
          colorTextRight="inherit"
          titleLeft={
            'Découvrez un monde de richesses culturelles et de pratiques sociales à travers le français'
          }
          contentLeft={
            "L'apprentissage du Français Langue Étrangère (FLE) ouvre la porte à une multitude de cultures et de pratiques sociales. Notre collection de ressources FLE axées sur les cultures et les sociétés francophones est conçue pour enrichir vos leçons et stimuler l'engagement de vos apprenants avec des sujets pertinents et captivants."
          }
          titleRight={'Des ressources adaptées à tous les niveaux'}
          contentRight={
            "Les ressources FLE publiées sur IFprofs sont soigneusement choisies pour correspondre aux différents niveaux de compétence, depuis les débutants jusqu'aux apprenants avancés. Elles permettent de travailler les compétences orales et écrites en abordant des sujets variés tels que : le sport, la gastronomie, les fêtes et les traditions, le cinéma et les séries, la musique, l'usage des technologies, les arts, la littérature, mais aussi les questions environnementales, d'égalité femmes-hommes, d'inclusion."
          }
        />
      )}
      {category === 'category.fle_communication' && (
        <DoubleTextBanner
          colorLeft="tuile.100"
          colorTextLeft="inherit"
          colorRight="tuile.10"
          colorTextRight="inherit"
          titleLeft={
            "Rendez l'apprentissage de la langue française plus attractif"
          }
          contentLeft={
            "Notre sélection de ressources FLE vise le développement des compétences linguistiques et de communication en français des élèves de manière ludique et attractive, tout en recherchant l'efficacité."
          }
          titleRight={'Des supports diversifiés pour un apprentissage efficace'}
          contentRight={
            <>
              Communication : stimulez les interactions et abordez les nombreux
              actes de langage avec des activités variées.
              <br />
              Grammaire : abordez les règles grammaticales et exercez vos élèves
              à travers de nombreux supports.
              <br />
              Vocabulaire : élargissez le lexique de vos apprenants et facilitez
              son apprentissage avec de multiples ressources.
              <br />
              Phonétique : aidez vos élèves avec la prononciation en français
              pour mieux comprendre et se faire comprendre.
            </>
          }
        />
      )}
      {category === 'category.dnl' && (
        <DoubleTextBanner
          colorLeft="tuile.100"
          colorTextLeft="inherit"
          colorRight="tuile.10"
          colorTextRight="inherit"
          titleLeft={'Ressources DNL - Bilinguisme et excellence pédagogique'}
          contentLeft={
            "L'enseignement bilingue ou des Disciplines Non Linguistiques (DNL), est un pilier fondamental de l'éducation plurilingue. Les ressources DNL répertoriées sur IFprofs couvrent différentes matières enseignées en français : les mathématiques, la littérature, les arts, les sciences, l'histoire-géographie, le sport. Ceraines visent également à faciliter une approche transdisciplinaire."
          }
          titleRight={null}
          contentRight={null}
        />
      )}
    </Box>
  );
}

function Recherche({
  resourcesSearch,
  defaultFilters,
  filters,
  facets,
  initialSearch = null,
  category = null,
  CECRLevel = null,
  page,
  algoliaCategories,
}: {
  resourcesSearch: any;
  defaultFilters: any[];
  filters: any[];
  facets?: [];
  initialSearch?: string;
  category?: string;
  CECRLevel?: string;
  page: number;
  algoliaCategories: { searchResult };
}) {
  const { query } = useRouter();
  const [headerSearch, setHeaderSearch] = useState('');

  const categoriesForSubheader = useSubHeaderCategoriesFromAlgoliaFacetSearch(
    algoliaCategories
  );

  const [currentSearch, setCurrentSearch] = useState('');

  const {
    isOpen: isFilterModalOpen,
    onOpen: onFilterModalOpen,
    onClose: onFilterModalClose,
  } = useDisclosure();

  const breadcrumbLabel = useMemo<string | null>(() => {
    if (category) {
      return resourceTranslation[category];
    } else if (CECRLevel) {
      return resourceTranslation['cecr_level'][CECRLevel];
    } else {
      return 'Recherche';
    }
  }, [category, CECRLevel]);

  const pageTitle = useMemo<string>(() => {
    if (category) {
      return `${resourceTranslation[category]} - Ressources`;
    }
    if (CECRLevel) {
      return `${resourceTranslation['cecr_level'][CECRLevel]} - Ressources`;
    }
    return 'Ressources';
  }, [category]);

  useEffect(() => {
    if (query.s !== undefined) {
      const decodedSearch = decodeURIComponent(query.s as string);
      setCurrentSearch(decodedSearch);
      setHeaderSearch(decodedSearch);
    }
  }, [query]);

  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');

  const breadcrumbItems = useMemo(() => {
    const response: BreadcrumbItemProps[] = [
      { label: 'Accueil', href: '/' },
      { label: 'Ressources', href: '/ressources' },
    ];

    // Ajout conditionnel des éléments de breadcrumb
    if (!category && !CECRLevel) {
      response.push({ label: 'Recherche' });
    }

    if (category) {
      response.push({ label: resourceTranslation[category] });
    }

    if (CECRLevel) {
      response.push({ label: resourceTranslation['cecr_level'][CECRLevel] });
    }

    return response;
  }, [category, CECRLevel]);

  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Ressource',
        page_level1: 'Ressources',
        page_level2: breadcrumbLabel,
      }}
    >
      <Head>
        <title>{pageTitle} - IFprofs</title>
        {category && categoryDescriptions[category] && (
          <meta name="description" content={categoryDescriptions[category]} />
        )}
      </Head>

      {isLargerThan1024 && (
        <Header mainNavItemActive={NavCategory.Ressources}>
          <HeaderResourceForm
            categories={categoriesForSubheader}
            search={headerSearch}
            setSearch={setHeaderSearch}
          />
        </Header>
      )}
      {!isLargerThan1024 && <Header />}

      <HeaderResourceForm
        categories={categoriesForSubheader}
        search={headerSearch}
        setSearch={setHeaderSearch}
        isRessourcesHomePageMobileOnly
      />

      <Breadcrumb breadcrumbItems={breadcrumbItems}>
        <BreadcrumbItem label="Accueil" href="/" />
        <BreadcrumbItem label="Ressources" href="/ressources" />
        {!category && !CECRLevel && <BreadcrumbItem label="Recherche" />}
        {category && (
          <>
            <BreadcrumbItem label={resourceTranslation[category]} />
          </>
        )}
        {CECRLevel && (
          <>
            <BreadcrumbItem
              label={resourceTranslation['cecr_level'][CECRLevel]}
            />
          </>
        )}
      </Breadcrumb>

      <UIWidth>
        {!category && !CECRLevel && (
          <Chapo
            color="tuile.100"
            heading={'Votre recherche : ' + currentSearch}
            icon={
              <RessourcesPicto freeIconColor="currentColor" iconWidth="100%" />
            }
          />
        )}
        {category && (
          <Chapo
            color="tuile.100"
            heading={resourceTranslation[category]}
            icon={
              <RessourcesPicto freeIconColor="currentColor" iconWidth="100%" />
            }
          >
            <CategoryDescription category={category} />
          </Chapo>
        )}
        {CECRLevel && (
          <Chapo
            color="tuile.100"
            heading={resourceTranslation['cecr_level'][CECRLevel]}
            icon={
              <RessourcesPicto freeIconColor="currentColor" iconWidth="100%" />
            }
          ></Chapo>
        )}
      </UIWidth>
      <MobileBottomNav>
        <Button
          variant="tile"
          sx={{
            ...mobileBottomNavStyle,
            width: '100%',
          }}
          onClick={onFilterModalOpen}
        >
          Filtrer
          <Icon as={PiShuffleThin} boxSize="1rem" ml="0.625rem" />
        </Button>
      </MobileBottomNav>
      <SearchApp
        key={category || CECRLevel || initialSearch}
        searchPlaceholder="Rechercher une ressource"
        loadMoreLabel="Voir plus de ressources"
        results={resourcesSearch}
        filters={filters}
        defaultFilters={defaultFilters}
        facets={facets}
        page={page}
        itemsPerPage={ITEMS_PER_PAGE}
        variant="tile"
        isFilterModalOpen={isFilterModalOpen}
        onFilterModalClose={onFilterModalClose}
        onSearchChange={setCurrentSearch}
        contentAfter={category && <CategorySEOContent category={category} />}
        extraQueryParamsForDetailLink={{
          isFromLabelizedRessourcesPage: 'true',
        }}
        twoResultsPerRow
        showResultTypeBadges={false}
      />
    </FrontLayout>
  );
}

Recherche.getInitialProps = async (context: NextPageContextWithRedux) => {
  const privateGroupIds = context.reduxStore
    .getState()
    .privateGroups.groups.map(group => group.id);
  const page = context.query.page ? parseInt(context.query.page as string) : 1;
  let defaultFilters: Record<string, string[] | number[]> = {
    types: ['pedagogicalResource'],
    labelized: [1],
  };

  if (context.query.category) {
    defaultFilters = {
      ...defaultFilters,
      mainCategories: [context.query.category as string],
    };
  }
  if (context.query.CECRLevel) {
    defaultFilters = {
      ...defaultFilters,
      CECRLevels: [context.query.CECRLevel as string],
    };
  }

  const filters = queryStringToFilters(context.query.filters, {
    ...defaultFilters,
  });
  const facets = [
    'mainCategories',
    'CECRLevels',
    'subCategories',
    'publics',
    'skills',
  ];

  const [resourcesSearch, algoliaCategories] = await Promise.all([
    fetchSearch(String(context.query.s ?? ''), {
      filters: filters,
      privateGroupIds: privateGroupIds,
      pagination: {
        page: page,
        hitsPerPage: ITEMS_PER_PAGE,
      },
      facets: facets,
    }),
    fetchLabelizedMainCategories(),
  ]);

  return {
    resourcesSearch,
    defaultFilters,
    filters,
    facets,
    initialSearch: context.query.s,
    category: context.query.category,
    CECRLevel: context.query.CECRLevel,
    page,
    algoliaCategories,
  };
};

export default withUser(withPrivateGroups(Recherche));
